import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'

// Drives the board's horizontal scroll from one proxy scrollbar fixed to the
// bottom of the viewport. The content's own scrollbar is hidden; the fixed bar
// mirrors its scroll position so it's always reachable at the screen bottom.
export const StickyScrollArea = ({ children }: { children: ReactNode }) => {
  const contentRef = useRef<HTMLDivElement>(null)
  const innerRef = useRef<HTMLDivElement>(null)
  const barRef = useRef<HTMLDivElement>(null)
  const [contentWidth, setContentWidth] = useState(0)
  const [view, setView] = useState({ left: 0, width: 0 })

  const measure = useCallback(() => {
    const content = contentRef.current
    const inner = innerRef.current
    if (!content || !inner) return
    const rect = content.getBoundingClientRect()
    setContentWidth((prev) => (prev === inner.offsetWidth ? prev : inner.offsetWidth))
    setView((prev) =>
      prev.left === rect.left && prev.width === content.clientWidth
        ? prev
        : { left: rect.left, width: content.clientWidth },
    )
  }, [])

  useEffect(() => {
    measure()
    const content = contentRef.current
    const inner = innerRef.current
    if (!content || !inner) return
    const observer = new ResizeObserver(measure)
    observer.observe(content)
    observer.observe(inner)
    window.addEventListener('resize', measure)
    return () => {
      observer.disconnect()
      window.removeEventListener('resize', measure)
    }
  }, [measure])

  const syncFromContent = () => {
    if (barRef.current && contentRef.current) {
      barRef.current.scrollLeft = contentRef.current.scrollLeft
    }
  }
  const syncFromBar = () => {
    if (contentRef.current && barRef.current) {
      contentRef.current.scrollLeft = barRef.current.scrollLeft
    }
  }

  const overflowing = contentWidth > view.width + 1

  return (
    <>
      <div
        ref={contentRef}
        onScroll={syncFromContent}
        className="overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        <div ref={innerRef} className="w-max">
          {children}
        </div>
      </div>
      {overflowing && (
        <div
          ref={barRef}
          onScroll={syncFromBar}
          className="fixed bottom-0 z-30 h-4 overflow-x-auto overflow-y-hidden border-t border-[#EEEEEE] bg-white"
          style={{ left: view.left, width: view.width }}
        >
          <div style={{ width: contentWidth, height: 1 }} />
        </div>
      )}
    </>
  )
}
